// What the site's tags may do in this browser: the banner for visitors we ask
// first, and the panel it shares with the "Your privacy choices" link on the
// privacy page. The rules and the tags live in public/index.html
// (window.bdConsent), because they have to run before anything else on the page.
import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { trapTab } from "./booking/a11y";
import { currentRef } from "./referral";

export const OPEN_CHOICES_EVENT = "bd:open-privacy-choices";

export function openPrivacyChoices(e) {
  e?.preventDefault?.();
  window.dispatchEvent(new CustomEvent(OPEN_CHOICES_EVENT));
}

const KINDS = [
  {
    id: "analytics",
    title: "Analytics",
    text: "Google Analytics and Microsoft Clarity show how people use the site, including session recordings with anything you type masked.",
  },
  {
    id: "advertising",
    title: "Advertising",
    text: "The Meta Pixel measures our advertising and lets us show ads to people who have visited.",
  },
  {
    id: "visitors",
    title: "Visitor identification",
    text: "Instantly and RB2B try to work out which person and company visited, so we can follow up.",
  },
  {
    id: "referrals",
    title: "Specialist link",
    text: "Remembers the specialist who sent you for 90 days, so they get credit if your company becomes a customer.",
    onlyWithRef: true,
  },
];

const everything = (on) => Object.fromEntries(KINDS.map((kind) => [kind.id, on]));

// What each kind of tag leaves in a browser.
const TRACKER_DATA = {
  analytics: /^(_ga|_gid|_gat|_gcl|_clck|_clsk|_cltk|_uet)/,
  advertising: /^(_fbp|_fbc)/,
  visitors: /^(_reb2b|ec_b2b)/,
};
// The Apollo tag was removed on Nov 4 2026. This is what it left behind.
const OLD_APOLLO_DATA = /^(apolloAnonId|6a455fa9ecbf67001483ef0a_)/;

/** Removes what the given kinds of tag left in this browser. */
export function clearTrackerData(kinds = Object.keys(TRACKER_DATA)) {
  const patterns = kinds.map((kind) => TRACKER_DATA[kind]).filter(Boolean);
  const host = window.location.hostname;
  document.cookie
    .split(";")
    .map((cookie) => cookie.split("=")[0].trim())
    .filter((name) => patterns.some((pattern) => pattern.test(name)))
    .forEach((name) => {
      [`; domain=${host}`, `; domain=.${host}`, ""].forEach((domain) => {
        document.cookie = `${name}=; Max-Age=0; path=/${domain}`;
      });
    });
  try {
    Object.keys(window.localStorage)
      .filter((key) => OLD_APOLLO_DATA.test(key) || patterns.some((pattern) => pattern.test(key)))
      .forEach((key) => window.localStorage.removeItem(key));
  } catch {}
}

function Switch({ id, on, onChange }) {
  return (
    <button
      type="button"
      className={`pcSwitch${on ? " isOn" : ""}`}
      role="switch"
      aria-checked={on}
      aria-labelledby={`pc-${id}`}
      aria-describedby={`pc-${id}-text`}
      onClick={() => onChange(!on)}
    >
      <span className="pcSwitchKnob" />
    </button>
  );
}

function ChoicesPanel({ consent, onChoose, onClose }) {
  const sheet = useRef(null);
  const opener = useRef(document.activeElement);
  const [draft, setDraft] = useState(() =>
    Object.fromEntries(KINDS.map((kind) => [kind.id, consent.permitted(kind.id)])),
  );

  useEffect(() => {
    const scroller = document.querySelector(".routeScroll");
    const root = document.getElementById("root");
    const overflow = scroller?.style.overflow || "";
    const back = opener.current;
    if (scroller) scroller.style.overflow = "hidden";
    root?.setAttribute("aria-hidden", "true");
    root?.setAttribute("inert", "");
    sheet.current?.focus();
    return () => {
      if (scroller) scroller.style.overflow = overflow;
      root?.removeAttribute("aria-hidden");
      root?.removeAttribute("inert");
      back?.focus?.();
    };
  }, []);

  const off = window.bdNoTrack;
  const signal = navigator.globalPrivacyControl;
  const kinds = KINDS.filter((kind) => !kind.onlyWithRef || currentRef());

  return createPortal(
    <div
      className="pcOverlay"
      onMouseDown={(e) => e.target === e.currentTarget && onClose()}
    >
      <div
        className="pcSheet"
        role="dialog"
        aria-modal="true"
        aria-labelledby="pc-title"
        tabIndex={-1}
        ref={sheet}
        onKeyDown={(e) => {
          if (e.key === "Escape") onClose();
          else trapTab(e, sheet.current);
        }}
      >
        <h2 className="pcTitle" id="pc-title">
          Your privacy choices
        </h2>

        {off ? (
          <>
            <p className="pcText">
              {signal
                ? "Opt-out honored. Your browser sends Global Privacy Control, so these tools are turned off."
                : "These tools are turned off here."}
            </p>
            <div className="pcActions">
              <button type="button" className="pcBtn" onClick={onClose}>
                Close
              </button>
            </div>
          </>
        ) : (
          <>
            <p className="pcText">
              Choose what may run in this browser. You can change this at any
              time from our{" "}
              <a className="pcLink" href="/privacy">
                Privacy Policy
              </a>
              .
            </p>
            <ul className="pcKinds">
              {kinds.map((kind) => (
                <li className="pcKind" key={kind.id}>
                  <div>
                    <div className="pcKindTitle" id={`pc-${kind.id}`}>
                      {kind.title}
                    </div>
                    <p className="pcKindText" id={`pc-${kind.id}-text`}>
                      {kind.text}
                    </p>
                  </div>
                  <Switch
                    id={kind.id}
                    on={draft[kind.id]}
                    onChange={(on) => setDraft((was) => ({ ...was, [kind.id]: on }))}
                  />
                </li>
              ))}
            </ul>
            <div className="pcActions">
              <button type="button" className="pcBtn" onClick={() => onChoose(everything(true))}>
                Accept all
              </button>
              <button type="button" className="pcBtn" onClick={() => onChoose(everything(false))}>
                Reject all
              </button>
              <button type="button" className="pcBtn pcBtnQuiet" onClick={() => onChoose(draft)}>
                Save choices
              </button>
            </div>
          </>
        )}
      </div>
    </div>,
    document.body,
  );
}

const shouldAsk = (consent) =>
  Boolean(consent && consent.askFirst() === true && !consent.choices() && !window.bdNoTrack);

export default function CookieChoices() {
  const consent = window.bdConsent;
  const [asking, setAsking] = useState(() => shouldAsk(consent));
  // The page may still be waiting on the country lookup in public/index.html.
  const [answered, setAnswered] = useState(() => Boolean(consent) && consent.askFirst() !== null);
  const [panelOpen, setPanelOpen] = useState(false);

  useEffect(() => {
    if (!consent) return undefined;
    const onAnswer = () => {
      setAnswered(true);
      setAsking(shouldAsk(consent));
    };
    window.addEventListener("bd:asked", onAnswer);
    // It may have answered between the first render and this effect.
    if (consent.askFirst() !== null) onAnswer();
    return () => window.removeEventListener("bd:asked", onAnswer);
  }, [consent]);

  useEffect(() => {
    const open = () => setPanelOpen(true);
    window.addEventListener(OPEN_CHOICES_EVENT, open);
    return () => window.removeEventListener(OPEN_CHOICES_EVENT, open);
  }, []);

  // On every page: nothing stays behind for a kind that isn't allowed, including
  // from before a visitor was asked, or a last write as the previous page closed.
  useEffect(() => {
    if (!consent || !answered) return;
    clearTrackerData(KINDS.map((kind) => kind.id).filter((id) => !consent.permitted(id)));
  }, [consent, answered]);

  if (!consent) return null;

  const choose = (next) => {
    const off = KINDS.map((kind) => kind.id).filter((id) => !next[id]);
    // A tag that has run can't be unloaded, so stopping one takes a fresh page.
    const stopping = off.some((id) => consent.ran(id));
    if (!next.analytics && consent.ran("analytics")) {
      // Google and Clarity otherwise send a last hit as the page closes.
      window["ga-disable-G-P82V2GBWES"] = true;
      window.clarity?.("stop");
    }
    consent.save(next);
    clearTrackerData(off);
    if (stopping) {
      window.location.reload();
      return;
    }
    setAsking(false);
    setPanelOpen(false);
  };

  return (
    <>
      {asking && !panelOpen && (
        <section className="pcBar" aria-label="Cookie choices">
          <p className="pcBarText">
            We&apos;d like to use cookies and similar tools for analytics, session
            recordings, advertising and identifying visitors. They stay off unless
            you allow them. See our{" "}
            <a className="pcLink" href="/privacy">
              Privacy Policy
            </a>
            .
          </p>
          <div className="pcActions">
            <button type="button" className="pcBtn" onClick={() => choose(everything(true))}>
              Accept all
            </button>
            <button type="button" className="pcBtn" onClick={() => choose(everything(false))}>
              Reject all
            </button>
            <button type="button" className="pcBtn pcBtnQuiet" onClick={() => setPanelOpen(true)}>
              Choose
            </button>
          </div>
        </section>
      )}
      {panelOpen && (
        <ChoicesPanel
          consent={consent}
          onChoose={choose}
          onClose={() => setPanelOpen(false)}
        />
      )}
    </>
  );
}
